import { useState, useEffect, useRef } from "react";
import "./style.scss";
import { useForm } from "react-hook-form";
import Switch from "@components/Switch";
import Button from "@components/Button";
import Input from "@components/Input";
import Textarea from "@components/TextArea";
import Dropdown from "@components/Dropdown";
import chevron from "./img/chevron.svg";
import PagesHeader from "@components/PagesHeader";
import { sendContactForm } from "@api/consumer";
import InputError from "@components/InputError/InputError";
import NiceModal from "@ebay/nice-modal-react";
import { useQueryClient } from "@tanstack/react-query";
import { EasterEgg } from "@pages/EasterEggPage";

const tematValidationRules = {
  required: {
    value: true,
    message: "Gebe das Thema deiner Frage an",
  },
};

const messageValidationRules = {
  required: {
    value: true,
    message: "Nachrichteninhalt eingeben",
  },
};

const testContestValidationRules = {
  required: {
    value: true,
    message: "Zustimmung erforderlich",
  },
};

export default function ContactPage() {
  const queryClient = useQueryClient();
  const userData = queryClient.getQueryData(["user-details"]) ?? {};
  const initialCategory = "Eine Kategorie auswählen";
  const [category, setCategory] = useState(initialCategory);
  const [marke, setMarke] = useState("Wähle eine Marke");
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [markeDropdownOpen, setMarkeDropdownOpen] = useState(false);
  const categoryDropdownRef = useRef(null);
  const markeDropdownRef = useRef(null);

  const {
    handleSubmit,
    formState: { errors, touchedFields, isSubmitting },
    register,
    setValue,
    setError,
    clearErrors,
    reset,
    control,
  } = useForm({
    mode: "onTouched",
    reValidateMode: "onChange",
  });

  useEffect(() => {
    if (userData?.consumer?.brands) {
      if (
        userData.consumer.brands.includes("glo") &&
        !userData.consumer.brands.includes("vuse")
      ) {
        setMarke("glo™");
      } else if (
        userData.consumer.brands.includes("vuse") &&
        !userData.consumer.brands.includes("glo")
      ) {
        setMarke("Vuse");
      } else {
        setMarke("Wähle eine Marke");
      }
    }
  }, [userData]);

  const resetForm = () => {
    reset();
    setCategory(initialCategory);
    if (
      userData?.consumer?.brands.includes("glo") &&
      !userData.consumer.brands.includes("vuse")
    ) {
      setMarke("glo™");
    } else if (
      userData?.consumer?.brands.includes("vuse") &&
      !userData.consumer.brands.includes("glo")
    ) {
      setMarke("Vuse");
    } else {
      setMarke("Wähle eine Marke");
    }
  };

  useEffect(() => {
    if (isSubmitting && category === initialCategory) {
      setError("category", {
        type: "required",
        message: "Eine Kategorie auswählen",
      });
    }
  }, [isSubmitting, category]);

  useEffect(() => {
    if (isSubmitting && (marke === "Wähle eine Marke" || !marke)) {
      setError("marke", {
        type: "required",
        message: "Eine Marke auswählen",
      });
    }
  }, [isSubmitting, marke]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        categoryDropdownRef.current &&
        !categoryDropdownRef.current.contains(event.target)
      ) {
        setCategoryDropdownOpen(false);
      }
      if (
        markeDropdownRef.current &&
        !markeDropdownRef.current.contains(event.target)
      ) {
        setMarkeDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const successCallback = () => {
    resetForm();
    NiceModal.show("success-send-form-modal");
  };

  const onSubmit = (data) => {
    const { title, message } = data;
    const payload = {
      contact: {
        title,
        category,
        message,
        brand: marke,
      },
    };

    sendContactForm({ payload, successCallback });
  };

  const categoryOptions = [
    "Nicht erhaltene Punkte",
    "Problem mit dem Gutschein",
    "Technische Probleme",
    "Andere",
  ];

  const markeOptions = ["glo™", "Vuse", "glo™ & Vuse"];

  return (
    <section className="contact-page">
      <PagesHeader>
        <strong>Kontakt</strong>
      </PagesHeader>
      <div className="contact-wrapper">
        <div className="form-wrapper">
          <p className="desc">
            Hast du irgendwelche <strong>Fragen oder Anregungen?</strong>
          </p>
          <div className="mobile-bg"></div>
          <p className="sub-desc">
            Wir sind bereit, deine Fragen zu beantworten und dir zu helfen,
            falls du Hilfe brauchst.
          </p>
          {/* <EasterEgg ident={"halloween2025_5"} direction="left" /> */}
          <form
            className="form"
            noValidate
            onSubmit={handleSubmit(onSubmit)}
            autoComplete="off"
          >
            <label className="form-title">
              <Input
                errors={errors["title"]}
                touched={touchedFields["title"]}
                type="title"
                name="title"
                placeholder={"Gib den Betreff der Frage ein"}
                label={"Thema"}
                validationRules={tematValidationRules}
                register={register}
              />
            </label>

            <br />
            <label className="form-category">
              Kategorie
              <div ref={categoryDropdownRef}>
                <Dropdown
                  className={
                    errors.category ? "form-dropdown error" : "form-dropdown"
                  }
                  summary={
                    <div
                      className="category-wrapper"
                      onClick={() =>
                        setCategoryDropdownOpen(!categoryDropdownOpen)
                      }
                    >
                      <h2
                        className={`category ${
                          category !== initialCategory
                            ? "category-selected"
                            : ""
                        }`}
                      >
                        {category}
                      </h2>
                      <img
                        alt="chevron"
                        src={chevron}
                        className={`chevron ${
                          categoryDropdownOpen ? "chevron-rotated" : ""
                        }`}
                      />
                    </div>
                  }
                  open={categoryDropdownOpen}
                  details={
                    <div
                      className={`dropdown-options ${
                        categoryDropdownOpen ? "dropdown-options-shown" : ""
                      }`}
                    >
                      {categoryOptions.map((option, index) => (
                        <button
                          type="button"
                          key={index}
                          onClick={() => {
                            setCategoryDropdownOpen(false);
                            setCategory(option);
                            clearErrors("category");
                          }}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  }
                />
              </div>
              {errors.category?.message && (
                <InputError errorMessage={errors.category?.message} />
              )}
            </label>

            <br />
            <label className="form-marke">
              Marke
              <div ref={markeDropdownRef}>
                {userData?.consumer?.brands.includes("glo") &&
                !userData.consumer.brands.includes("vuse") ? (
                  <h2 className="non-editable">glo™</h2>
                ) : userData?.consumer?.brands.includes("vuse") &&
                  !userData.consumer.brands.includes("glo") ? (
                  <h2 className="non-editable">Vuse</h2>
                ) : (
                  <Dropdown
                    className={
                      errors.marke ? "form-dropdown error" : "form-dropdown"
                    }
                    summary={
                      <div
                        className="marke-wrapper"
                        onClick={() => setMarkeDropdownOpen(!markeDropdownOpen)}
                      >
                        <h2
                          className={`marke ${
                            marke !== "Wähle eine Marke" ? "marke-selected" : ""
                          }`}
                        >
                          {marke}
                        </h2>
                        <img
                          alt="chevron"
                          src={chevron}
                          className={`chevron ${
                            markeDropdownOpen ? "chevron-rotated" : ""
                          }`}
                        />
                      </div>
                    }
                    open={markeDropdownOpen}
                    details={
                      <div
                        className={`dropdown-options ${
                          markeDropdownOpen ? "dropdown-options-shown" : ""
                        }`}
                      >
                        {markeOptions.map((option, index) => (
                          <button
                            type="button"
                            key={index}
                            onClick={() => {
                              setMarkeDropdownOpen(false);
                              setMarke(option);
                              clearErrors("marke");
                            }}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    }
                  />
                )}
              </div>
              {errors.marke?.message && (
                <InputError errorMessage={errors.marke?.message} />
              )}
            </label>

            <br />
            <label className="form-text">
              <Textarea
                errors={errors["message"]}
                touched={touchedFields["message"]}
                type="message"
                name="message"
                placeholder={"Stelle eine Frage"}
                label={"Nachricht"}
                validationRules={messageValidationRules}
                register={register}
                maxLength={500}
              />
            </label>

            <br />
            <div className="form-checkbox">
              <div className="switch-wrapper">
                <Switch
                  errors={errors["testContest"]}
                  validationRules={testContestValidationRules}
                  name="testContest"
                  label={`Ich bin kein Roboter`}
                  register={register}
                  required
                />
              </div>
              <Button submit>Senden</Button>
            </div>
            <br />
          </form>
        </div>
        <div className="info-wrapper">
          <div className="info-wrapper-inner">
            <p className="info-head">Unser Standort</p>
            <p className="info-des">British American Tobacco Germany GmbH</p>
            <p className="info-bottom">
              Alsterufer 4<br /> 20354 Hamburg
            </p>
          </div>
          <div className="info-wrapper-inner">
            <p className="info-head">Kontaktiere uns</p>
            <p className="info-des">service@bat.de</p>
            {/* <p className="info-bottom">Open Mon – Sun, 8:00 – 22:00</p> */}
          </div>
        </div>
      </div>
    </section>
  );
}
